import { Component, ElementRef, forwardRef, input, output, Renderer2, viewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'k-input-message',
    imports: [ReactiveFormsModule],
    templateUrl: './k-input-message.component.html',
    styleUrl: './k-input-message.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KInputMessageComponent),
            multi: true,
        },
    ],
})
export class KInputMessageComponent implements ControlValueAccessor {
    private renderer = inject(Renderer2);

    public readonly placeholder = input.required<string>();
    public readonly submit = output<void>();
    public readonly maxHeight = input<string>('140px');

    private refTextArea = viewChild.required<ElementRef<HTMLDivElement>>('textArea');

    private onTouchedCallback: () => void = () => {};
    private onChangeCallback: (value: string) => void = () => {};

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    protected onKeyDown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'Enter':
                if (!event.shiftKey) {
                    event.preventDefault();
                    this.submit.emit();
                }
                break;

            case 'Escape':
                this.clearMessage();
                break;

            default:
                break;
        }
    }

    protected onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const text = (event.clipboardData?.getData('text/plain') || '').replace(/<[^>]*>/g, '');
        const replacedText = text.replace(/(\r\n|\n|\r)/gm, '<br />');
        this.insertHtmlAtCursor(replacedText);
        this.updateFormControl();
    }

    protected onInput(event: Event): void {
        this.updateFormControl();
    }

    protected onBlur(event: FocusEvent): void {
        this.onTouchedCallback();
    }

    // ControlValueAccessor implementation
    writeValue(value: string): void {
        const normalizedValue = value || '';
        this.renderer.setProperty(this.refTextArea().nativeElement, 'innerHTML', normalizedValue);
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.renderer.setProperty(this.refTextArea().nativeElement, 'contentEditable', !isDisabled);
    }

    private updateFormControl(): void {
        const element = this.refTextArea().nativeElement;
        let htmlContent = element.innerHTML;

        // Normaliza conteúdo vazio
        const textContent = this.htmlToText(htmlContent);

        if (!textContent.trim()) {
            // Se o texto está vazio, limpa completamente o HTML
            this.renderer.setProperty(element, 'innerHTML', '');
        }

        this.onChangeCallback(textContent);
    }

    private htmlToText(html: string): string {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return tempDiv.textContent || tempDiv.innerText || '';
    }

    private insertHtmlAtCursor(html: string): void {
        let sel: Selection | null, range: Range;
        const document = window.document;

        if (window.getSelection) {
            sel = window.getSelection();
            if (sel && sel.getRangeAt && sel.rangeCount) {
                range = sel.getRangeAt(0);
                range.deleteContents();

                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;

                const fragment = document.createDocumentFragment();
                let lastNode: Node | null = null;

                while (tempDiv.firstChild) {
                    lastNode = fragment.appendChild(tempDiv.firstChild);
                }

                range.insertNode(fragment);

                if (lastNode) {
                    const newRange = range.cloneRange();
                    newRange.setStartAfter(lastNode);
                    newRange.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(newRange);
                }
            }
        }
    }

    public clearMessage(): void {
        this.writeValue('');
        this.updateFormControl();
    }

    public focus(): void {
        this.refTextArea().nativeElement.focus();
    }
}
